import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { showToast } from '@/components';
import { ChainId, getJSONRPCUrl, isSupportedChain } from '@/lib';
import { getStorage, StorageKeys, useBoolean, useStorage } from '@/utils';
import { ethers } from 'ethers';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isMobile as checkIsMobile } from 'web3modal';
import { AccountDialog } from './AccountDialog';
import { connectableWallets, ConnectDialog } from './ConnectDialog';
import getProvider from './providers';
export * from './providers';
const isMobile = checkIsMobile();
export const Web3Context = React.createContext({});
export const useWeb3Context = () => useContext(Web3Context);
export const Web3Provider = ({ children }) => {
    const [accountDialogVisible, setAccountDialogVisible] = useState(false);
    const [storedChainId, setStoredChainId, removeStoredChainId] = useStorage(StorageKeys.ChainId);
    const [storedWalletId, setStoredWalletId, removeStoredWalletId] = useStorage(StorageKeys.WalletId);
    const [isConnecting, setIsConnecting] = useState(false);
    const [{ providerChainId, ethersProvider, account }, setWeb3State] = useState({});
    const [dialogVisible, openConnectionDialog, closeConnectionDialog] = useBoolean();
    const setWeb3Provider = useCallback(async (prov) => {
        try {
            if (!prov)
                return;
            const provider = new ethers.providers.Web3Provider(prov);
            const accounts = await provider.send('eth_accounts', []);
            if (accounts.length === 0)
                return;
            const chainId = Number(prov.chainId);
            if (!account) {
                const signer = provider.getSigner();
                setWeb3State({
                    account: await signer.getAddress(),
                    ethersProvider: provider,
                    providerChainId: chainId,
                });
            }
            else {
                setWeb3State((state) => ({
                    ...state,
                    ethersProvider: provider,
                    providerChainId: chainId,
                }));
            }
        }
        catch (err) {
            showToast({
                variant: 'error',
                title: 'Fails to set provider',
                message: err.message,
            });
        }
    }, [account]);
    const setDefaultProvider = useCallback(() => {
        const provider = new ethers.providers.JsonRpcProvider(getJSONRPCUrl());
        setWeb3State((state) => ({
            ...state,
            ethersProvider: provider,
            providerChainId: ChainId.Mainnet,
        }));
    }, []);
    const disconnect = useCallback(() => {
        setWeb3State({});
        removeStoredChainId();
        removeStoredWalletId();
    }, []);
    const connectWeb3 = useCallback(async (chainId, walletType) => {
        try {
            setIsConnecting(true);
            const provider = await getProvider(isMobile ? undefined : walletType);
            if (chainId && chainId !== parseInt(provider.chainId, 16)) {
                await provider.request({
                    method: 'wallet_switchEthereumChain',
                    params: [
                        {
                            chainId: `0x${chainId.toString(16)}`,
                        },
                    ],
                });
            }
            await setWeb3Provider(provider);
            const updateWeb3Provider = () => setWeb3Provider(provider);
            provider.on('accountsChanged', updateWeb3Provider);
            provider.on('chainChanged', updateWeb3Provider);
            provider.on('disconnect', disconnect);
        }
        finally {
            setIsConnecting(false);
        }
    }, [setWeb3Provider, disconnect]);
    useEffect(() => {
        const walletId = getStorage(StorageKeys.WalletId);
        (async () => {
            const walletType = connectableWallets.find((w) => w.id === walletId)?.type;
            if (walletId && walletType) {
                const provider = await getProvider(walletType);
                if (provider) {
                    connectWeb3(parseInt(provider.chainId, 16), walletType);
                }
            }
            else {
                setDefaultProvider();
            }
        })();
    }, [connectWeb3, setDefaultProvider]);
    const openAccountDialog = useCallback(() => {
        setAccountDialogVisible(true);
    }, []);
    const closeAccountDialog = useCallback(() => {
        setAccountDialogVisible(false);
    }, []);
    const value = useMemo(() => {
        const isNotSupportedChain = providerChainId !== undefined && !isSupportedChain(providerChainId);
        return {
            openConnectionDialog,
            closeConnectionDialog,
            openAccountDialog,
            closeAccountDialog,
            disconnect,
            account,
            ethersProvider,
            providerChainId,
            connectWeb3,
            isMetaMask: !!ethersProvider?.provider?.isMetaMask,
            isConnecting,
            isNotSupportedChain,
        };
    }, [
        disconnect,
        account,
        ethersProvider,
        connectWeb3,
        providerChainId,
        isConnecting,
        openAccountDialog,
        closeAccountDialog,
    ]);
    return (_jsxs(Web3Context.Provider, { value: value, children: [children, _jsx(ConnectDialog, { open: dialogVisible, onClose: closeConnectionDialog, chainId: storedChainId ?? ChainId.Mainnet, walletId: storedWalletId ?? undefined, onSelect: ({ chainId, walletId }) => {
                    setWeb3State((state) => ({
                        ...state,
                        providerChainId: chainId,
                    }));
                    const walletType = connectableWallets.find((w) => w.id === walletId)?.type;
                    connectWeb3(chainId, walletType).then(() => {
                        setStoredChainId(chainId);
                        setStoredWalletId(walletId);
                    });
                    closeConnectionDialog();
                } }), _jsx(AccountDialog, { open: accountDialogVisible, onClose: closeAccountDialog })] }));
};
